import React, { useState, useEffect, useContext } from 'react';
import { UserDataContext } from '../../App';
import { Grid, Typography, Button, InputAdornment, Box } from '@mui/material';
import { MySidebar } from "../../libs/theme";
import { styled } from "@mui/material/styles";
import { ValidatorForm, TextValidator } from "react-material-ui-form-validator";
import { getText } from "../../libs/utils";
import REST from "../../libs/API";
import { Lock, ShowPass, HidePass } from "../../assets/images/icons";
import { ContentBox } from '../../libs/theme';
import ConfirmDeleteDialog from '../../components/ConfirmDeleteDialog';

const MyContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  [theme.breakpoints.only('xs')]: {
    flexDirection: 'column'
  }
}))

const ChangedSidebar = styled(MySidebar)(({ theme }) => ({
  marginLeft: '40px',
  [theme.breakpoints.only('xs')]: {
    borderLeft: '0',
    borderTop: `1px solid ${theme.palette.primary.borderColor}`,
    margin: '30px 0 0',
    padding: '35px 0 0',
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
    "span:last-child": {
      margin: 0
    }
  }
}))

const Settings = () => {
  const userData = useContext(UserDataContext);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ password: "", passwordRepeat: "", newPassword: "" })
  const [showPasswords, setShowPasswords] = useState({ password: "password", passwordRepeat: "password", newPassword: "password" });
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteData, setDeleteData] = useState({});

  useEffect(() => {
    ValidatorForm.addValidationRule('isPasswordMatch', (value) => {
      if (value !== formData.newPassword) {
        return false;
      }
      return true;
    }, []);
  }, [formData.newPassword]);

  useEffect(() => {
    return () => {
      ValidatorForm.removeValidationRule('isPasswordMatch');
    };
  }, []);

  const handleSubmit = () => {
    const { password, newPassword } = formData;
    setLoading(true);
    REST.changePassword(userData.token, password, newPassword)
      .then((data) => {
        if (!data.success) {
          window.showMessage(data.data[0].message);
        } else {
          window.showMessage(getText('uAccChangePassMsg'));
          setFormData({ password: "", passwordRepeat: "", newPassword: "" })
        }
        setLoading(false);
      });
  }

  const deleteAccount = () => {
    REST.deleteAccount(userData.token).then(data => {
      if (!data.success) {
        window.showMessage(data.data[0].message)
      } else {
        window.showMessage(getText("accSuccDeleted"));
        userData.deAuth();
      }
    })
  };

  const deleteOrders = () => {
    REST.clearOrdersHistory(userData.token).then(data => {
      if (!data.success) {
        window.showMessage(data.data[0].message)
      } else {
        window.showMessage(getText("accHistoryDeleted"))
      }
    })
  };

  const deleteWallets = () => {
    REST.clearReqs(userData.token).then(data => {
      if (!data.success) {
        window.showMessage(data.data[0].message)
      } else {
        window.showMessage(getText("accReqsDeleted"))

      }
    })
  };

  const confirmDeleteData = {
    account: {
      title: getText('accTabSetDeleteLabel'),
      content: getText('accClearAcc'),
      submitHandler: () => {
        deleteAccount();
        setDeleteDialog(false);
      }
    },
    orders: {
      title: getText('accTabSetDelHistoryLabel'),
      content: getText('accClearExcHist'),
      submitHandler: () => {
        deleteOrders();
        setDeleteDialog(false);
      }
    },
    wallets: {
      title: getText('accTabSetDelReqsLabel'),
      content: getText('accClearReqs'),
      submitHandler: () => {
        deleteWallets();
        setDeleteDialog(false);
      }
    },
  }

  const confirmDeleteHandler = (key) => {
    setDeleteData(confirmDeleteData[key]);
    setDeleteDialog(true);
  }

  return (
    <ContentBox sx={{ minHeight: 486, display: 'flex' }}>
      <MyContainer>
        <Box>
          <Typography variant='h4'>{getText('uAccChangePassLabel')}</Typography>
          <ValidatorForm
            onSubmit={handleSubmit}
            instantValidate={false}
          >
            <Grid container spacing={{ xs: 0, sm: 2 }} sx={{
              "& .MuiInputBase-root, button": {
                height: "64px"
              }
            }}>
              <Grid item lg={5} md={6} sm={10} xs={12}>
                <TextValidator
                  fullWidth
                  placeholder={getText('loginFormPassLabel')}
                  variant="standard"
                  name="password"
                  type={showPasswords.password}
                  onChange={(event) =>
                    setFormData({
                      ...formData,
                      [event.target.name]: event.target.value,
                    })
                  }
                  validators={["required", "minStringLength:8"]}
                  errorMessages={[getText('inputRequired'), getText('inputPassMin')]}
                  value={formData.password}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment
                        position="start"
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                          showPasswords.password === "password"
                            ? setShowPasswords({ ...showPasswords, password: "text" })
                            : setShowPasswords({ ...showPasswords, password: "password" })
                        }
                      >
                        {showPasswords.password === "password" ? <ShowPass /> : <HidePass />}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item lg={5} md={6} sm={10} xs={12}>
                <TextValidator
                  fullWidth
                  placeholder={getText('accChangePassFieldNew')}
                  variant="standard"
                  name="newPassword"
                  type={showPasswords.newPassword}
                  onChange={(event) =>
                    setFormData({
                      ...formData,
                      [event.target.name]: event.target.value,
                    })
                  }
                  validators={["required", "minStringLength:8"]}
                  errorMessages={[getText('inputRequired'), getText('inputPassMin')]}
                  value={formData.newPassword}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment
                        position="start"
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                          showPasswords.newPassword === "password"
                            ? setShowPasswords({ ...showPasswords, newPassword: "text" })
                            : setShowPasswords({ ...showPasswords, newPassword: "password" })
                        }
                      >
                        {showPasswords.newPassword === "password" ? <ShowPass /> : <HidePass />}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item lg={5} md={6} sm={10} xs={12}


              >
                <TextValidator
                  fullWidth
                  placeholder={getText('accChangePassFieldRepeat')}
                  variant="standard"
                  name="passwordRepeat"
                  type={showPasswords.passwordRepeat}
                  onChange={(event) =>
                    setFormData({
                      ...formData,
                      [event.target.name]: event.target.value,
                    })
                  }
                  validators={['isPasswordMatch', 'required']}
                  errorMessages={[getText('inputPassDistinct'), getText('inputRequired')]}
                  value={formData.passwordRepeat}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment
                        position="start"
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                          showPasswords.passwordRepeat === "password"
                            ? setShowPasswords({ ...showPasswords, passwordRepeat: "text" })
                            : setShowPasswords({ ...showPasswords, passwordRepeat: "password" })
                        }
                      >
                        {showPasswords.passwordRepeat === "password" ? <ShowPass /> : <HidePass />}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item lg={5} md={6} sm={10} xs={12}>
                <Button variant="contained" fullWidth size="large" type="submit" disabled={loading} sx={{ margin: '6px 0', padding: '12px 32px' }}>
                  {getText('accChangePassSendBtn')}
                </Button>
              </Grid>
            </Grid>

          </ValidatorForm>
        </Box>

        <ChangedSidebar>
          <Typography variant='link' onClick={() => confirmDeleteHandler('orders')}>{getText('clearHistoryButton')}</Typography>
          <Typography variant='link' onClick={() => confirmDeleteHandler('wallets')}>{getText('deleteWalletsButton')}</Typography>
          <Typography variant='link' onClick={() => confirmDeleteHandler('account')}>{getText('delectAccountButton')}</Typography>
        </ChangedSidebar>
      </MyContainer>

      <ConfirmDeleteDialog
        title={deleteData.title}
        content={deleteData.content}
        openDialog={deleteDialog}
        setOpenDialog={setDeleteDialog}
        submitHandler={deleteData.submitHandler}
      />

    </ContentBox>
  )
}

export default Settings